import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiUpdateUser } from '../../utils/api';
import { getAuthToken } from '../../utils/auth';

export const EditUserModal = ({ user, onClose }) => {
    const { darkMode } = useTheme();
    const [formData, setFormData] = useState({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'user'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.email.trim()) {
            setError('Имя и Email обязательны');
            return;
        }

        setLoading(true);
        const token = getAuthToken();
        const data = await apiUpdateUser(token, user.id, formData);
        setLoading(false);

        if (data.success) {
            onClose();
        } else {
            setError(data.error || 'Ошибка при обновлении пользователя');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]"
            onClick={onClose}
        >
            <div
                className={`rounded-xl shadow-2xl w-full max-w-md p-6 ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                }`}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Редактирование пользователя
                    </h2>
                    <button
                        onClick={onClose}
                        className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className={`mb-4 p-3 rounded-lg text-sm ${
                            darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-600'
                        }`}>
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Логин (нельзя изменить)
                            </label>
                            <input
                                type="text"
                                value={user.username}
                                disabled
                                className={`w-full px-4 py-2 rounded-lg bg-gray-100 ${
                                    darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                } cursor-not-allowed`}
                            />
                        </div>
                        <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Имя *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                                }`}
                                placeholder="Полное имя"
                            />
                        </div>
                        <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Email *
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                                }`}
                                placeholder="user@example.com"
                            />
                        </div>
                        <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Роль *
                            </label>
                            <select
                                value={formData.role}
                                onChange={(e) => setFormData({...formData, role: e.target.value})}
                                className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                                }`}
                            >
                                <option value="user">Пользователь</option>
                                <option value="admin">Администратор</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                    }">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                                darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                            } ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                        >
                            {loading ? 'Сохранение...' : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Сохранить
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </motion.div>
    );
};